/**
 * VELGERNE COMPOSEREN ÅPNER (KOI Del 4: `GET /v1/mentions/suggest`, `GET /v1/links/suggest`).
 *
 * =================================================================================================
 * TO RUTER, FORDI DET ER TO SPØRSMÅL.
 *
 * `@` spør «hvem», og svaret er mennesker med et navn, en rolle og en avdeling — nok til å skille
 * to som heter Thomas fra hverandre uten å åpne en side.
 *
 * `#` spør «hva», og svaret er objekter av tre slag med hver sin tittel og sin egen rute. De kunne
 * vært én rute med en `type`-parameter, og det ville vært feil: velgeren viser dem BLANDET og
 * sortert på treffkvalitet, så en klient med tre kall måtte flettet tre lister i riktig rekkefølge
 * selv — og den ene som gjør det feil er den som viser oppgaver først fordi de kom først.
 *
 * =================================================================================================
 * DE ER OPPSLAG, IKKE LISTER.
 *
 * Ingen markør, ingen `hasMore`, et hardt tak. En velger som åpnes på et tastetrykk skal svare med
 * de beste ti og ikke med en side å bla i — den som ikke finner det de leter etter, skriver ett
 * bokstav til. Det er den samme formen `listPerformanceChatMentionsQuery` har, og av den samme
 * grunnen: det er et oppslag som skjer mens noen skriver, ikke noe en sidevisning betaler for.
 *
 * `editionId` SNEVRER, DEN FILTRERER IKKE BORT. En årgang gjør «Rigge scene 2» til den riktige av
 * tre med samme navn; uten den er svaret hele huset, som er riktig for en samtale som ikke hører
 * til et år.
 */
import { z } from 'zod';

import { LINK_OBJECT_TYPES } from '../chorus-md.js';

/** Ti er nok til å velge fra og få til å lese. Se hodet for hvorfor det ikke er en side. */
export const MAX_SUGGESTIONS = 10;

export const suggestQuerySchema = z
  .object({
    /** Det som er skrevet etter `@` eller `#`. Tom streng er et gyldig «vis de nyeste». */
    q: z.string().trim().max(80).optional(),
    editionId: z.uuid().optional(),
  })
  .meta({ id: 'SuggestQuery' });

/**
 * Et menneske velgeren kan sette inn.
 *
 * `personId` er det som havner i `@[[person:…|Navn]]`; `displayName` er teksten. `role` og
 * `department` er der for å SKILLE, ikke for å pynte — to som heter Thomas er nøyaktig tilfellet
 * velgeren finnes for.
 */
export const mentionSuggestionSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    displayName: z.string(),
    role: z.string().nullable(),
    department: z.string().nullable(),
  })
  .meta({ id: 'MentionSuggestion' });

export const mentionSuggestResponseSchema = z
  .object({ items: z.array(mentionSuggestionSchema) })
  .meta({ id: 'MentionSuggestResponse' });

/**
 * Et objekt velgeren kan lenke til.
 *
 * `subtitle` er det som gjør et treff entydig — datoen på en programpost, statusen på en oppgave —
 * og den er nullbar fordi ikke alt har en.
 */
export const linkSuggestionSchema = z
  .object({
    objectType: z.enum(LINK_OBJECT_TYPES),
    objectId: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
  })
  .meta({ id: 'LinkSuggestion' });

export const linkSuggestResponseSchema = z
  .object({ items: z.array(linkSuggestionSchema) })
  .meta({ id: 'LinkSuggestResponse' });

export type SuggestQuery = z.infer<typeof suggestQuerySchema>;
export type MentionSuggestion = z.infer<typeof mentionSuggestionSchema>;
export type MentionSuggestResponse = z.infer<typeof mentionSuggestResponseSchema>;
export type LinkSuggestion = z.infer<typeof linkSuggestionSchema>;
export type LinkSuggestResponse = z.infer<typeof linkSuggestResponseSchema>;
